import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../../api/client'
import styles from './AuthShell.module.css'

// The pieces the email-code steps share: sign-up (AuthShell) and "forgot
// password" (ForgotPassword). The API sends 6 digits, live 10 minutes, and
// allows a new one a minute after the last (pkg/userland/auth/emailcode.go).

export const RESEND_AFTER_SECONDS = 60

export function wellFormedCode(code: string): boolean {
  return /^\d{6}$/.test(code.trim())
}

/** Seconds until a new code may be asked for; `start` after each send. */
export function useResendCountdown(): { left: number; start: () => void } {
  const [left, setLeft] = useState(0)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearInterval(timer.current)
    },
    []
  )
  const start = () => {
    if (timer.current) clearInterval(timer.current)
    setLeft(RESEND_AFTER_SECONDS)
    timer.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1 && timer.current) {
          clearInterval(timer.current)
          timer.current = null
        }
        return Math.max(0, s - 1)
      })
    }, 1000)
  }
  return { left, start }
}

export function CodeField({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        Code
      </label>
      <input
        id={id}
        className={styles.input}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6-digit code"
        aria-label="Code"
        value={value}
        // digits only, at most six: a pasted "123 456" or "Code: 123456" works
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
      />
    </div>
  )
}

export function ResendCode({
  left,
  busy,
  onResend,
}: {
  left: number
  busy: boolean
  onResend: () => void
}) {
  return (
    <button
      type="button"
      className={`${styles.textLink} ${styles.inlineLink}`}
      disabled={busy || left > 0}
      onClick={onResend}
    >
      {left > 0 ? `Send a new code in ${left} s` : 'Send a new code'}
    </button>
  )
}

export function isCodeInvalid(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'code_invalid'
}

/** One short sentence for a failed code request or a refused code. */
export function emailCodeFailMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'code_invalid') return 'The code is wrong or expired'
    if (err.code === 'code_rate_limited' || err.status === 429) {
      return 'Too many codes requested. Wait a minute and try again.'
    }
    if (err.code === 'email_unavailable' || err.status === 503) {
      return 'Email is not available right now. Try again later.'
    }
    if (err.code === 'password_too_weak') return err.message
    return err.message
  }
  return 'Could not send the code. Try again.'
}
